import { useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { motion, useAnimationControls, useReducedMotion } from "motion/react";

export interface RevealAnimationCoordinator {
  recentKeys: string[];
  durations: Map<string, number>;
  bounces: Map<string, number>;
  runners: Map<string, (duration: number, bounce: number) => Promise<void>>;
  scrollVelocity: number;
  lastScrollAt: number;
  pendingFinalReveal: boolean;
  finalRevealTimer?: number;
}

export const createRevealAnimationCoordinator = (): RevealAnimationCoordinator => ({
  recentKeys: [],
  durations: new Map(),
  bounces: new Map(),
  runners: new Map(),
  scrollVelocity: 0,
  lastScrollAt: 0,
  pendingFinalReveal: false,
});

export const requestCoordinatedFinalReveal = (coordinator: RevealAnimationCoordinator) => {
  coordinator.pendingFinalReveal = true;
  if (coordinator.finalRevealTimer != null) {
    window.clearTimeout(coordinator.finalRevealTimer);
  }
  // Let Virtuoso leave scroll-seek mode and mount the real rows first. If it
  // has not done so yet, pendingFinalReveal remains set and the next mounted
  // reveal will schedule this check again.
  coordinator.finalRevealTimer = window.setTimeout(() => {
    coordinator.finalRevealTimer = undefined;
    const latestKey = coordinator.recentKeys.at(-1);
    const latestRunner = latestKey ? coordinator.runners.get(latestKey) : undefined;
    if (!latestRunner) return;
    coordinator.pendingFinalReveal = false;
    // Promote the animation from its current position. Never reset a row that
    // has already appeared, which would visibly replay its reveal.
    void latestRunner(0.34, 0.34);
  }, 32);
};

export const ScrollCardReveal = ({
  children,
  scrollRoot,
  revealKey,
  revealedKeys,
  animationsEnabled,
  topSpacing = false,
  deferMount = false,
  revealDelayMs = 0,
  rootMargin = "0px 0px -24% 0px",
  placeholder = null,
  exemptInitialViewport = true,
  animationDelayMs = 0,
  onReveal,
  revealInitialViewport = false,
  queueReveal,
  animationCoordinator,
}: {
  children: React.ReactNode;
  scrollRoot: HTMLElement;
  revealKey: string;
  revealedKeys: Set<string>;
  animationsEnabled: boolean;
  topSpacing?: boolean;
  deferMount?: boolean;
  revealDelayMs?: number;
  rootMargin?: string;
  placeholder?: React.ReactNode;
  exemptInitialViewport?: boolean;
  animationDelayMs?: number;
  onReveal?: () => void;
  revealInitialViewport?: boolean;
  queueReveal?: (reveal: () => void) => () => void;
  animationCoordinator?: RevealAnimationCoordinator;
}) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const animatedElementRef = useRef<HTMLDivElement | null>(null);
  const animationControls = useAnimationControls();
  const animationGeneration = useRef(0);
  const [revealed, setRevealed] = useState(() => revealedKeys.has(revealKey));
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element || revealedKeys.has(revealKey)) {
      setRevealed(true);
      return;
    }

    const cardRect = element.getBoundingClientRect();
    const rootRect = scrollRoot.getBoundingClientRect();
    const initiallyOnScreen = cardRect.bottom > rootRect.top && cardRect.top < rootRect.bottom;
    const reveal = () => {
      const commitReveal = () => {
        revealedKeys.add(revealKey);
        setShouldAnimate(animationsEnabled);
        setRevealed(true);
        onReveal?.();
      };
      if (animationCoordinator) {
        const previousIndex = animationCoordinator.recentKeys.indexOf(revealKey);
        if (previousIndex >= 0) {
          animationCoordinator.recentKeys.splice(previousIndex, 1);
        }
        animationCoordinator.recentKeys.push(revealKey);

        const isActivelyScrolling = performance.now() - animationCoordinator.lastScrollAt < 120;
        const velocity = isActivelyScrolling ? animationCoordinator.scrollVelocity : 0;
        const speedFactor = Math.min(1, Math.max(0, (velocity - 0.3) / 2.7));
        // Slow scrolling keeps the dramatic wave. At high velocity the whole
        // tail contracts, including the distance between its three finishes.
        const newestDuration = 0.34 - 0.12 * speedFactor;
        const trailingDuration = 0.21 - 0.105 * speedFactor;
        const finishGap = 0.02 - 0.015 * speedFactor;
        const durationsByAge = [newestDuration, trailingDuration, trailingDuration - finishGap];
        const bouncesByAge = [0.34, 0.12, 0.06];
        animationCoordinator.recentKeys.forEach((key, index, keys) => {
          const age = keys.length - 1 - index;
          const duration = durationsByAge[age] ?? 0.045;
          const bounce = bouncesByAge[age] ?? 0;
          animationCoordinator.durations.set(key, duration);
          animationCoordinator.bounces.set(key, bounce);
          const runner = animationCoordinator.runners.get(key);
          if (runner) void runner(duration, bounce);
        });

        // Only the latest three rows need to retain graded physics state.
        while (animationCoordinator.recentKeys.length > 3) {
          const staleKey = animationCoordinator.recentKeys.shift();
          if (staleKey) {
            animationCoordinator.durations.delete(staleKey);
            animationCoordinator.bounces.delete(staleKey);
          }
        }
        commitReveal();
      } else {
        commitReveal();
      }
    };
    const revealAfterDelay = () => {
      if (queueReveal) return queueReveal(reveal);
      if (revealDelayMs > 0) return window.setTimeout(reveal, revealDelayMs);
      reveal();
      return undefined;
    };
    if (exemptInitialViewport && !animationsEnabled && initiallyOnScreen) {
      revealedKeys.add(revealKey);
      setRevealed(true);
      return;
    }
    if (revealInitialViewport && initiallyOnScreen) {
      const timer = revealAfterDelay();
      return () => {
        if (typeof timer === "number") window.clearTimeout(timer);
        else timer?.();
      };
    }

    if (typeof IntersectionObserver === "undefined") {
      revealedKeys.add(revealKey);
      setRevealed(true);
      return;
    }
    let cancelPendingReveal: number | (() => void) | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        cancelPendingReveal = revealAfterDelay();
      },
      {
        root: scrollRoot,
        rootMargin,
        threshold: 0.2,
      },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (typeof cancelPendingReveal === "number") {
        window.clearTimeout(cancelPendingReveal);
      } else {
        cancelPendingReveal?.();
      }
    };
  }, [
    animationCoordinator,
    animationsEnabled,
    exemptInitialViewport,
    onReveal,
    queueReveal,
    revealDelayMs,
    revealInitialViewport,
    revealKey,
    revealedKeys,
    rootMargin,
    scrollRoot,
  ]);

  useLayoutEffect(() => {
    if (!revealed) {
      animationControls.set({ rotateX: -90 });
      return;
    }

    const settleTransform = () => {
      if (!animatedElementRef.current) return;
      animatedElementRef.current.style.transform = "none";
      animatedElementRef.current.style.willChange = "auto";
    };
    if (!shouldAnimate || prefersReducedMotion) {
      animationControls.set({ rotateX: 0 });
      settleTransform();
      return;
    }

    const runAnimation = async (analysisDuration?: number, analysisBounce = 0) => {
      const generation = ++animationGeneration.current;
      await animationControls.start(
        { rotateX: 0 },
        analysisDuration != null
          ? {
              type: "spring",
              visualDuration: analysisDuration,
              bounce: analysisBounce,
            }
          : {
              type: "spring",
              stiffness: 155,
              damping: 15,
              mass: 1.35,
              restSpeed: 0.15,
              restDelta: 0.08,
              delay: animationDelayMs / 1000,
            },
      );
      if (generation !== animationGeneration.current) return;
      settleTransform();
    };

    if (animationCoordinator) {
      const runner = (duration: number, bounce: number) => runAnimation(duration, bounce);
      animationCoordinator.runners.set(revealKey, runner);
      if (animationCoordinator.pendingFinalReveal) {
        requestCoordinatedFinalReveal(animationCoordinator);
      }
      void runner(
        animationCoordinator.durations.get(revealKey) ?? 0.24,
        animationCoordinator.bounces.get(revealKey) ?? 0.34,
      );
    } else {
      void runAnimation();
    }

    return () => {
      animationGeneration.current += 1;
      animationCoordinator?.runners.delete(revealKey);
    };
  }, [
    animationControls,
    animationCoordinator,
    animationDelayMs,
    prefersReducedMotion,
    revealKey,
    revealed,
    shouldAnimate,
  ]);

  return (
    <Box
      ref={elementRef}
      sx={{
        height: "100%",
        pb: 0,
        pt: topSpacing ? 2 : 0,
        boxSizing: "border-box",
        visibility: revealed ? "visible" : "hidden",
      }}
    >
      <motion.div
        ref={animatedElementRef}
        initial={{ rotateX: revealed ? 0 : -90 }}
        animate={animationControls}
        style={{
          height: "100%",
          transformOrigin: "50% 0%",
          transformPerspective: 640,
          backfaceVisibility: "hidden",
          willChange: shouldAnimate && revealed ? "transform" : "auto",
        }}
      >
        {deferMount && !revealed ? placeholder : children}
      </motion.div>
    </Box>
  );
};
